import { ComponentType } from '@angular/cdk/overlay';
import { computed, inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarDismiss, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { arraySignal } from 'src/shared/array-signal';
import { CelebrationConfig } from './celebration.component';

interface SnackBarQueuedRequest<T = unknown> {
  message?: string;
  action?: string;
  component?: ComponentType<T>;
  config?: MatSnackBarConfig;
  entry: SnackBarQueueEntry;
}

export type SnackBarRequest<T = unknown> = Omit<SnackBarQueuedRequest<T>, 'entry'>;

interface SnackBarQueueEntry {
  afterDismissed: Observable<MatSnackBarDismiss>;
  afterOpened: Observable<void>;
  cancel: boolean;
}

type SnackBarRef = MatSnackBarRef<{ serviceSnackBarRequest: SnackBarQueuedRequest; }>;

@Injectable()
export class CelebrationService {
  readonly #matSnackBar = inject(MatSnackBar);

  readonly #queue = arraySignal<SnackBarQueuedRequest>([]);
  readonly currentQueue = computed(() => [...this.#queue()]);
  readonly #cancelStandard$ = new Subject<void>();

  open(message: string, action?: string, config?: MatSnackBarConfig): SnackBarQueueEntry {
    return this.#processRequest({ message, action, config });
  }

  openCelebration<T = unknown>(component: ComponentType<T>, config?: MatSnackBarConfig): SnackBarQueueEntry {
    return this.#processRequest({ component, config });
  }

  #processRequest(request: SnackBarRequest): SnackBarQueueEntry {

    const entry: SnackBarQueueEntry = {
      afterDismissed: new Subject<MatSnackBarDismiss>(),
      afterOpened: new Subject<void>(),
      cancel: false,
    };

    const queuedRequest: SnackBarQueuedRequest = {
      ...request,
      entry,
    };

    if (!this.#matSnackBar._openedSnackBarRef) {
      this.#openSnackBar(queuedRequest);
    } else {
      const currentRef = this.#matSnackBar._openedSnackBarRef as SnackBarRef;
      if (currentRef.instance.serviceSnackBarRequest == null) {
        this.#cancelStandard$.next();
        currentRef.afterDismissed()
          .pipe(
            takeUntil(this.#cancelStandard$),
            take(1))
          .subscribe(() => this.#processQueue());
      }
      this.#queue.updatePush(queuedRequest);
    }

    return entry;
  }

  #openSnackBar(request: SnackBarQueuedRequest): void {

    if (this.#matSnackBar._openedSnackBarRef) {
      const currentRef = this.#matSnackBar._openedSnackBarRef as SnackBarRef;
      if (currentRef.instance.serviceSnackBarRequest == null) {
        this.#attachDismissHandler(currentRef, request);
      } else {
        currentRef.instance.serviceSnackBarRequest.entry.cancel = true;
      }
    }

    if (this.#matSnackBar._openedSnackBarRef) {
      this.#matSnackBar.dismiss();
    }

    let ref: SnackBarRef;
    if (request.component) {
      ref = this.#matSnackBar.openFromComponent(request.component, request.config) as SnackBarRef;
    } else {
      ref = this.#matSnackBar.open(request.message, request.action, request.config) as unknown as SnackBarRef;
    }

    ref.instance.serviceSnackBarRequest = request;
    this.#attachDismissHandler(ref, request);
  }

  #attachDismissHandler(ref: SnackBarRef, request?: SnackBarQueuedRequest): void {
    ref.afterDismissed().subscribe((result) => {
      const afterDismissed = request?.entry.afterDismissed as Subject<MatSnackBarDismiss>;
      const dismissedRef = this.#matSnackBar._openedSnackBarRef as SnackBarRef;

      if (afterDismissed) {
        afterDismissed.next(result);
        afterDismissed.complete();
      }

      if (dismissedRef == null) {
        this.#processQueue();
      } else if (dismissedRef.instance.serviceSnackBarRequest == null) {
        this.#attachDismissHandler(dismissedRef);
      } else if (!dismissedRef.instance.serviceSnackBarRequest.entry.cancel) {
        this.#processQueue();
      }
    });
  }

  async #processQueue(): Promise<void> {
    if (!this.#queue().length) {
      return;
    }

    let request = this.#queue.updateShift();
    while (this.#queue().length > 0 && request?.entry.cancel) {
      request = this.#queue.updateShift();
    }

    if (!request) {
      return;
    }

    await new Promise(resolve => setTimeout(resolve, (request.config?.data as CelebrationConfig)?.delay ?? 0));

    if (this.#matSnackBar._openedSnackBarRef) {
      const currentRef = this.#matSnackBar._openedSnackBarRef as SnackBarRef;
      if (currentRef.instance.serviceSnackBarRequest != null) {
        this.#queue.updatePush(request);
        return;
      }
    }

    this.#openSnackBar(request);
  }

  clearQueue(): void {
    this.#queue.set([]);
  }

  dismissSnackBar(clearQueue = false): void {
    this.#matSnackBar.dismiss();
    if (clearQueue) {
      this.clearQueue();
    }
  }

}
